import { Component, OnInit } from '@angular/core';
import { EntityCollectionService, EntityServices } from '@ngrx/data';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import * as fromDataServices from '../../data-services';
import { LoginModel } from '../../data-services/manage-users';
import * as fromLookups from '../../lookups';
import { CalcType, IServiceItem, ITask } from '../../lookups';
import { CalcTypeService } from '../../lookups/calc-type.service';
import { PayrollItem } from '../../payroll/item/store';
import {
    DynamicDialogConfig,
    DynamicDialogRef,
    SelectItem
} from '../../shared/efaact-primeng.module';
import { LoggingService } from '../../shared/services/logging/logging.service';

@Component({
    selector: 'ef-project-budget-add-container',
    templateUrl: './project-budget-add-container.component.html',
    styleUrls: ['./project-budget-add-container.component.scss'],
    standalone: false
})
export class ProjectBudgetAddContainerComponent implements OnInit {
    tasks$: Observable<ITask[]>;
    serviceItems$: Observable<IServiceItem[]>;
    payrollItem$: Observable<PayrollItem[]>;
    users$: Observable<LoginModel[]>;
    costTypes$: Observable<CalcType[]>;
    costCenterSelectItems$: Observable<SelectItem[]>;
    tasksSelectItems$: Observable<SelectItem[]>;
    serviceItemsSelectItems$: Observable<SelectItem[]>;
    usersSelectItems$: Observable<SelectItem[]>;
    costTypesSelectItems$: Observable<SelectItem[]>;
    private Contractlistid: string;
    private ManageUsersModelDS: EntityCollectionService<fromDataServices.ManageUsers.LoginModel>;

    constructor(
        private store: Store,
        private log: LoggingService,
        private calcTypeService: CalcTypeService,
        public entityServices: EntityServices,

        private dialogRef: DynamicDialogRef,
        public config: DynamicDialogConfig
    ) {
        this.log.verbose({
            type: 'ProjectBudgetAddContainerComponent::',
            config
        });
        this.Contractlistid = this.config.data.contract.Contractlistid;
        this.store.dispatch(
            new fromLookups.LoadContractTasks({
                contractlistid: this.Contractlistid
            })
        );
        this.ManageUsersModelDS =
            entityServices.getEntityCollectionService<fromDataServices.ManageUsers.LoginModel>(
                fromDataServices.ManageUsers.entityName
            );
    }

    ngOnInit(): void {
        this.store.dispatch(
            new fromLookups.LoadServiceItems(true, false, true)
        );
        this.ManageUsersModelDS.getWithQuery({
            ShowInactive: 'false',
            UserFilterID: '7'
        });
        this.setupObservables();
    }
    setupObservables(): void {
        this.tasks$ = this.store.pipe(
            select(fromLookups.getContractTasks),
            map(items => items.filter(item => item.IsActive))
        );
        this.serviceItems$ = this.store.pipe(
            select(fromLookups.getServiceItemsLookup),
            map(items => items.filter(item => item.IsActive))
        );
        this.users$ = this.ManageUsersModelDS.entities$.pipe(
            map(items => items.filter(item => item.IsActive))
        );
        this.payrollItem$ = this.store.pipe(
            select(fromLookups.getPayrollItemsLookup),
            map(items => items.filter(item => item.IsActive))
        );
        this.costTypes$ = this.calcTypeService.getCalcType();
        this.costCenterSelectItems$ = this.store.pipe(
            select(fromLookups.getCostCenters),
            map(items =>
                items.map(item => ({
                    label: item.GroupName,
                    value: item.GroupID
                }))
            )
        );

        this.tasksSelectItems$ = this.tasks$.pipe(
            filter(items => Array.isArray(items)),
            tap(tasks =>
                this.log.verbose({
                    type: 'ProjectBudgetAddContainerComponent:tasksSelectItems$',
                    tasks,
                    Contractlistid: this.Contractlistid
                })
            ),
            map(items =>
                items
                    .filter(item => !!item)
                    .filter(item => item.ContractListID === this.Contractlistid)
                    .map(item => ({
                        label: item.TaskLongName,
                        value: item.TaskListID
                    }))
                    .sort(this.sortLabels)
            )
        );
        this.serviceItemsSelectItems$ = this.serviceItems$.pipe(
            map(items =>
                items
                    .map(item => ({
                        label: item.ServiceItem,
                        value: item.SIListID
                    }))
                    .sort(this.sortLabels)
            )
        );
        this.usersSelectItems$ = this.users$.pipe(
            map(items =>
                items
                    .map(item => ({
                        label: item.EmployeeName,
                        value: item.EmployeeListID
                    }))
                    .sort(this.sortLabels)
            )
        );
        this.costTypesSelectItems$ = this.costTypes$.pipe(
            map(ct =>
                ct
                    .filter(x => x.CalcType !== 'FP')
                    .map(x => ({ label: x.CalcDesc, value: x.CalcType }))
                    .sort(this.sortLabels)
            )
        );
    }
    sortLabels = (a: SelectItem, b: SelectItem) =>
        a.label?.localeCompare(b.label ?? '') ?? 0;
    onSave(budget): void {
        this.log.verbose({
            type: 'ProjectBudgetAddContainerComponent:onSave',
            budget
        });
        this.dialogRef.close(budget);
    }
    onCancel(): void {
        this.dialogRef.close();
    }
}
